import { h, Fragment } from "preact";
import { useMemo, useState } from "preact/hooks";
import { Plus, Minus, ArrowUpRightBox } from "../helpers/icons";

function parseManualLinks(product) {
  if (!product?.manualLinks) return [];
  try {
    const items = JSON.parse(product.manualLinks);
    return Array.isArray(items)
      ? items
          .filter((item) => item?.url)
          .map((item) => ({ url: item.url, text: item.text || "Manual" }))
      : [];
  } catch {
    return [];
  }
}

function buildRows(parts) {
  const rows = [];
  for (const part of parts) {
    const variants = Array.isArray(part.variants) ? part.variants : [];
    for (const variant of variants) {
      const parentTitles = Array.isArray(part.sparePartFor)
        ? part.sparePartFor.map((p) => p.title)
        : [];
      rows.push({
        variantId: variant.id,
        productTitle: part.title,
        productHandle: part.handle,
        variantTitle: variant.title !== "Default Title" ? variant.title : null,
        price: parseFloat(variant.price.amount),
        available: variant.availableForSale,
        image: variant.image || part.featuredImage,
        parentTitles,
      });
    }
  }
  return rows;
}

function RowImage({ image }) {
  if (!image) return <div class="replacement-parts__row-img" />;
  return (
    <div class="replacement-parts__row-img">
      <img src={image.url} alt={image.altText || ""} loading="lazy" />
    </div>
  );
}

function AddToCartButton({ variantId, available, adding, onAdd }) {
  if (!available) {
    return (
      <button
        type="button"
        class="button button--black replacement-parts__atc-btn replacement-parts__atc-btn--sold-out"
        disabled
      >
        Sold Out
      </button>
    );
  }
  return (
    <button
      type="button"
      class={`button button--black replacement-parts__atc-btn${adding ? " replacement-parts__atc-btn--adding" : ""}`}
      disabled={adding}
      onClick={onAdd}
    >
      {adding ? "Adding..." : "Add to Cart"}
    </button>
  );
}

function PartsTable({ rows, addingToCart, onAdd }) {
  return (
    <>
      {/* Desktop table */}
      <div
        class="replacement-parts__table"
        role="table"
        aria-label="Replacement parts"
      >
        <div class="replacement-parts__table-header" role="row">
          <span
            class="replacement-parts__table-col replacement-parts__table-col--part"
            role="columnheader"
          >
            Spare Part
          </span>
          <span
            class="replacement-parts__table-col replacement-parts__table-col--price"
            role="columnheader"
          >
            Price
          </span>
          <span
            class="replacement-parts__table-col replacement-parts__table-col--action"
            role="columnheader"
          />
        </div>
        {rows.map((row) => (
          <div
            key={row.variantId}
            class="replacement-parts__table-row"
            role="row"
          >
            <div
              class="replacement-parts__table-col replacement-parts__table-col--part"
              role="cell"
            >
              <RowImage image={row.image} />
              <div class="replacement-parts__row-name-wrap">
                <a
                  href={`/products/${row.productHandle}`}
                  class="replacement-parts__row-name"
                >
                  {row.productTitle}
                  {row.variantTitle && (
                    <span class="replacement-parts__row-variant">
                      {row.variantTitle}
                    </span>
                  )}
                </a>
                {row.parentTitles.length > 0 && (
                  <span class="replacement-parts__row-for">
                    For {row.parentTitles.join(", ")}
                  </span>
                )}
              </div>
            </div>
            <div
              class="replacement-parts__table-col replacement-parts__table-col--price"
              role="cell"
            >
              {window.formatMoney(row.price)}
            </div>
            <div
              class="replacement-parts__table-col replacement-parts__table-col--action"
              role="cell"
            >
              <AddToCartButton
                variantId={row.variantId}
                available={row.available}
                adding={!!addingToCart[row.variantId]}
                onAdd={() => onAdd(row.variantId)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Mobile accordion list */}
      <div class="replacement-parts__mobile-list">
        {rows.map((row) => (
          <conditional-details
            key={row.variantId}
            class="replacement-parts__accordion-item"
          >
            <details>
              <summary class="replacement-parts__accordion-summary">
                <RowImage image={row.image} />
                <span class="replacement-parts__row-name-wrap">
                  <span class="replacement-parts__row-name">
                    {row.productTitle}
                    {row.variantTitle && (
                      <span class="replacement-parts__row-variant">
                        {row.variantTitle}
                      </span>
                    )}
                  </span>
                  {row.parentTitles.length > 0 && (
                    <span class="replacement-parts__row-for">
                      For {row.parentTitles.join(", ")}
                    </span>
                  )}
                </span>
                <span
                  class="replacement-parts__accordion-icon replacement-parts__accordion-icon--plus"
                  aria-hidden="true"
                >
                  <Plus />
                </span>
                <span
                  class="replacement-parts__accordion-icon replacement-parts__accordion-icon--minus"
                  aria-hidden="true"
                >
                  <Minus />
                </span>
              </summary>
              <div data-content="" class="replacement-parts__accordion-content">
                <div className="replacement-parts__accordion-content-inner">
                  <div className="replacement-parts__accordion-content-item">
                    <span className="replacement-parts__accordion-label">
                      Price:
                    </span>
                    <span class="replacement-parts__accordion-price">
                      {window.formatMoney(row.price)}
                    </span>
                  </div>
                  <AddToCartButton
                    variantId={row.variantId}
                    available={row.available}
                    adding={!!addingToCart[row.variantId]}
                    onAdd={() => onAdd(row.variantId)}
                  />
                </div>
              </div>
            </details>
          </conditional-details>
        ))}
      </div>
    </>
  );
}

export default function ReplacementPartsResult({ type, product, parts }) {
  const [addingToCart, setAddingToCart] = useState({});

  const manualLinks = useMemo(() => parseManualLinks(product), [product]);
  const rows = useMemo(() => buildRows(parts || []), [parts]);

  const handleAddToCart = (variantId) => {
    const id = variantId.replace("gid://shopify/ProductVariant/", "");
    setAddingToCart((prev) => ({ ...prev, [variantId]: true }));
    window.addToCart([{ id, quantity: 1 }], {
      onAfterAdd: () =>
        setTimeout(
          () => setAddingToCart((prev) => ({ ...prev, [variantId]: false })),
          200,
        ),
      onError: () =>
        setAddingToCart((prev) => ({ ...prev, [variantId]: false })),
    });
  };

  return (
    <div class="replacement-parts__result">
      {type === "product" && product && (
        <>
          <div class="replacement-parts__result-header">
            <span class="replacement-parts__result-eyebrow">
              Replacement Parts for
            </span>
            <h2 class="replacement-parts__result-title">{product.title}</h2>
            {manualLinks.length > 0 && (
              <ul class="replacement-parts__result-manuals">
                {manualLinks.map((link) => (
                  <li key={link.url}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="replacement-parts__result-manual-link"
                    >
                      <span className="replacement-parts__result-manual-link-text">
                        {link.text}
                      </span>
                      <ArrowUpRightBox />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {product.featuredImage && (
            <div class="replacement-parts__result-hero">
              <img
                src={product.featuredImage.url}
                alt={product.featuredImage.altText || product.title}
                loading="lazy"
              />
            </div>
          )}
        </>
      )}

      {rows.length > 0 ? (
        <PartsTable
          rows={rows}
          addingToCart={addingToCart}
          onAdd={handleAddToCart}
        />
      ) : (
        <p class="replacement-parts__result-empty">No parts available.</p>
      )}
    </div>
  );
}
